import VisaApplicationFormComponent from '../components/visa-application-form.component';
import { VisaApplication, VisaApplicationUpdatePayload } from '../types/visa-application.types';
import {
  useVisaApplicationGet,
  useVisaApplicationUpdate,
} from '../hooks/use-visa-application.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import { useCallback, useMemo, useState } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import PageTitle from '@components/common/PageTitle';

interface FormSubmitHandler {
  (data: VisaApplicationUpdatePayload, resetForm: () => void): void;
}

const VisaApplicationUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const { data, isLoading, error } = useVisaApplicationGet(id ?? '');
  const { mutateAsync: update, isPending } = useVisaApplicationUpdate();

  const formDefaultValues = useMemo((): Partial<VisaApplicationUpdatePayload> => {
    if (!data?.data) return {};
    const app = data.data;
    // Handle visaStatus - it might be a Status object or a string ID
    const visaStatusId =
      typeof app.visaStatus === 'object' && app.visaStatus !== null
        ? (app.visaStatus as any).id || (app.visaStatus as any)._id
        : app.visaStatus;
    return {
      mrz: app.mrz,
      passportNumber: app.passportNumber,
      applicationNumber: app.applicationNumber,
      visaType: app.visaType,
      visaStatus: visaStatusId,
      visaUidNumber: app.visaUidNumber,
      visaFileNumber: app.visaFileNumber,
      visaIssueDate: app.visaIssueDate,
      visaExpiryDate: app.visaExpiryDate,
      visaName: app.visaName,
      visaSponsor: app.visaSponsor,
      clientData: app.clientData,
      applicationData: app.applicationData,
      employeeData: app.employeeData,
      passportData: app.passportData,
      eidData: app.eidData,
      visaData: app.visaData,
      driversLicense: app.driversLicense,
      contract: app.contract,
      recruiterInfo: app.recruiterInfo,
      religion: app.religion,
      snapshotTable: app.snapshotTable,
      mapping: app.mapping,
      flightInfo: app.flightInfo,
      ticketManagement: app.ticketManagement,
    };
  }, [data]);

  const onFormSubmit: FormSubmitHandler = useCallback(
    async (formData, resetForm) => {
      if (!id) return;
      setValidationErrors([]);
      try {
        await update({ id, data: formData });
        message.success(t('crud.update.success', { entity: t('visaApplications.title') }));
        navigate(-1);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.update.error', { entity: t('visaApplications.title') })
        );
        message.error(errorMessage);
      }
    },
    [id, update, t, navigate]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.update.title', { entity: t('visaApplications.title') })} />
        <HeaderRow
          title={t('crud.update.title', { entity: t('visaApplications.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: t('visaApplications.title') })} />
      <HeaderRow
        title={t('crud.update.title', { entity: t('visaApplications.title') })}
        showBackButton={true}
      />
      <div className="row mt-3">
        <VisaApplicationFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending || isLoading}
          defaultValues={formDefaultValues}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(VisaApplicationUpdateContainer, 'visaApplication.update');
